/**
 * useStudentSync
 *
 * Hydrates StudentStore from the backend on mount (when logged in, non-prototype).
 * Also seeds StudentStore from StorageService in prototype mode so the store
 * is always populated regardless of mode.
 *
 * Mount this once inside App.jsx (or any always-mounted auth-guarded component).
 */

import { useEffect } from "react";
import { AuthStore } from "./stores/AuthStore";
import { StudentStore } from "./stores/StudentStore";
import { StorageService } from "./services/storageService";
import api from "./api/apiClient";
import config from "../../config";

export function useStudentSync() {
  const isLoggedIn = AuthStore((s) => s.isLoggedIn);
  const currentUser = AuthStore((s) => s.currentUser);

  useEffect(() => {
    if (!isLoggedIn || !currentUser) return;

    if (config.appMode === "prototype") {
      // Seed from StorageService demo data
      const students = StorageService.read("students") as any[];
      const student = students?.find(
        (s) => s.userId === currentUser._id || s.email === currentUser.email,
      );
      if (student && !StudentStore.getState().student) {
        StudentStore.getState().setStudent(student);
      }
      return;
    }

    // Production: fetch from backend
    if (currentUser.userType !== "student") return;

    StudentStore.getState().setLoading(true);
    api
      .get("/students/me")
      .then((res) => {
        const remote = res.data?.data?.student;
        if (!remote) return;

        const local = StudentStore.getState().student;

        // Merge: backend is authoritative for profile fields, but career path
        // data (activeNodes, selectedBroadPaths) should never regress — keep
        // whichever source has more entries. This prevents a failed PATCH from
        // causing the next login/token-refresh to silently erase local progress.
        const merged = {
          ...remote,
          selectedBroadPaths:
            (remote.selectedBroadPaths?.length ?? 0) >= (local?.selectedBroadPaths?.length ?? 0)
              ? remote.selectedBroadPaths
              : local?.selectedBroadPaths,
          // activeNodes is not in the StudentProfile TS type so cast as any
          activeNodes:
            ((remote as any).activeNodes?.length ?? 0) >= ((local as any)?.activeNodes?.length ?? 0)
              ? (remote as any).activeNodes
              : (local as any)?.activeNodes,
        };

        StudentStore.getState().setStudent(merged);

        // Merge backend daily challenge history into localStorage.
        // Backend is authoritative: if a date exists in both, backend wins.
        const backendHistory: Record<string, string> =
          (remote as any).dailyChallengeHistory
            ? Object.fromEntries(
                Object.entries((remote as any).dailyChallengeHistory)
              )
            : {};
        if (Object.keys(backendHistory).length > 0) {
          try {
            const local: Record<string, string> = JSON.parse(
              localStorage.getItem('daily-challenge-history') ?? '{}'
            );
            const merged = { ...local, ...backendHistory };
            localStorage.setItem('daily-challenge-history', JSON.stringify(merged));
          } catch {
            // ignore parse errors
          }
        }
      })
      .catch(() => {})
      .finally(() => StudentStore.getState().setLoading(false));
  }, [isLoggedIn, currentUser]);
}
